import event, {EVENTS} from '../../lib/event';
import {focusBlock, getNodeInToolbox, getNodeInCodeWorkspace, getBlockRect} from '../highlight.jsx';
import {getAllBlocks, getBlockById, blocksHasSubstack, blocksInfo, matchBlock, blocksDesc} from '../blocks';
import StepsRuntime from '../steps-runtime';
import {workspaceSafeUndo, parseTranslate, moveBlockPosition} from '../utils';
import ScratchBlocks from 'scratch-blocks';
import Audio from "../../lib/audio";
import Cartoon from '../avatar-action';
import {BATCH_TIPS_MODE, GUIDE_MODE, TRAIN_MODE} from '../../lib/constant';
import focusElement from './focus-element';

/**
 * 引导用户从左侧 toolbox 拖动一个积木块到右侧 workspace
 * @param {object} source - 被拖动的积木块
 * @param {string} source.id - 被拖的积木块的blockId
 * @param {string|undefined} source.opcode - 当从左侧拖入积木块时，表示积木块的类型
 * @param {string|undefined} source.varName - 当opcode=data_variable时，表示变量名
 * @param {string|undefined} source.proccode - 当opcode=procedures_call时，表示函数名
 * @param {object} target - 目标位置
 * @param {string|undefined} target.id - 目标的blockId
 * @param {string|undefined} target.input - 如果是添加到父块内部，表示内部位置的key
 * @param {number|undefined} target.inputIndex - 如果时添加到父块内部，表示内部位置的index
 * @param {string|undefined} target.field - 如果是操作的父块fields中的某个参数，表示field的key
 * @param {number|undefined} target.fieldIndex - 如果是操作的父块fields中的某个参数，表示field的index
 * @param {object|undefined} target.position - 拖动到空白处的位置
 * @param {string} tips - 提示信息
 * @returns Promise
 */
export default function dragBlock({source, target, tips}, mode, _index) {
    //代码中通过mode是否为undefined来判断是否要显示聊天记录，因此这里不能初始化 if (mode === undefined) mode = stepsRuntime.mode;
    if (_index === undefined) _index = {stepIndex: stepsRuntime.stepIndex, actionIndex: stepsRuntime.actionIndex};

    if (mode) {
        const g = getNodeInToolbox(source);
        if (!tips) {
            if (source.opcode && blocksDesc[source.opcode]) {
                tips = '拖动『 ' + blocksDesc[source.opcode].name + ' 』积木块到目标位置';
            } else if (g) {
                tips = '拖动高亮积木块到目标位置';
            } else {
                tips = '拖动指定积木块到目标位置';
            }
        }
        window.stepsRuntime.insertTips(_index, tips);
        if (mode === BATCH_TIPS_MODE) {
            return new Promise(resolve => {
                resolve();
            })
        }else if (mode === TRAIN_MODE) {
            //尝试去定位
            return focusBlock({source, target, reject: () => {}, tips}, mode, _index);
        }
    }else {
        if (!tips) {
            tips = "拖动高亮积木块到目标位置";
        }
        Audio.tts(tips);
    }
    return new Promise((resolve, reject) => {
        const onAbort = ()=>{
            event.emit(EVENTS.CLEAR_COVER); //用于消除动画和遮罩
            event.off(EVENTS.END_DRAG_AND_MOVE, handleEndDrag);
            window.toolbox.flyout_.scrollbar_.isAvailable_ = true; //恢复toolbox滚动条
            reject('Step Abort');
        }
        event.once(EVENTS.STEP_ABORT, onAbort);

        focusBlock({source, target, reject, tips}, mode, _index).then(() => {
            window.toolbox.flyout_.scrollbar_.isAvailable_ = false; //禁用toolbox滚动条
        });
        const startBlocksCnt = getAllBlocks().length;
        const handleEndDrag = info => {
            window.toolbox.flyout_.scrollbar_.isAvailable_ = true; //恢复toolbox滚动条
            //const endBlocksCnt = getAllBlocks().length;
            const wrapResolve = () => {
                event.off(EVENTS.STEP_ABORT, onAbort);
                resolve(info.blockId);
                if (source.id && source.opcode) {
                    stepsRuntime.setBlockId(source.id, info.blockId);
                }
            };
            const wrapReject = (e) => {
                event.off(EVENTS.STEP_ABORT, onAbort);
                workspaceSafeUndo().then(() => {
                    reject(e);
                });
            };
            /*
            if (source.opcode) {
                if (endBlocksCnt <= startBlocksCnt) {
                    wrapReject('没有拖动到右侧代码区域。请将左侧高亮积木块，拖动到中间代码区的高亮位置');
                    return;
                }
            }else {
                if (endBlocksCnt != startBlocksCnt) {
                    wrapReject('请移动代码区高亮的块到目标位置，而不是从左侧拖入新的块或删除块');
                    return;
                }
            }*/

            const block = getBlockById(info.blockId);
            if (!block) { //没有正确拖入
                if (source.opcode) {
                    wrapReject('没有拖动到右侧代码区域。请将左侧高亮积木块，拖动到中间代码区的高亮位置');
                }else {
                    wrapReject('请移动代码区高亮的块到目标位置，而不是从左侧拖入新的块或删除块');
                }
                return;
            }
            //验证被拖动的块是否正确
            if (matchBlock(source, block)) {
                if (target.id) {
                    if (!block.parent) {
                        wrapReject('没有放到正确的位置，请将被拖拽积木块的左端，与目标高亮区域的左端，对齐放置');
                        return;
                    }else {
                        const parentBlock = getBlockById(block.parent);
                        if (matchBlock(target, parentBlock)) {
                            if (target.input) {
                                if (parentBlock.inputs && parentBlock.inputs[target.input] && parentBlock.inputs[target.input].block === info.blockId) {
                                    wrapResolve();
                                    return;
                                }else if (parentBlock.opcode === 'procedures_call') {

                                    //当target为自定义积木块时，这里的inputField可能跟录制时不一样，需要通过blockInputFieldIndex来校验
                                    let i = 0;
                                    let found = false;
                                    for (let field in parentBlock.inputs) {
                                        if (target.blockInputIndex != undefined) {
                                            if (i >= target.blockInputIndex) {
                                                found = true;
                                            }
                                        }else if (i >= target.inputIndex) { //兼容没有blockInputIndex时录制的历史脚本
                                            found = true;
                                        }
                                        if (found) {
                                            if (parentBlock.inputs[field] && parentBlock.inputs[field].block === info.blockId) {
                                                wrapResolve();
                                                return;
                                            }else {
                                                wrapReject('没有放到正确的位置，请将被拖拽积木块的左端，与目标高亮区域的左端，对齐放置');
                                                return;
                                            }
                                            break;
                                        }
                                        ++i;
                                    }

                                    /* input会发生变化，此方法不可行
                                    const inputBlock = parentBlock.inputs[stepsRuntime.getBlockId(target.input)];
                                    if (inputBlock) {
                                        if (inputBlock.block === info.blockId) {
                                            wrapResolve();
                                            return;
                                        }else {
                                            wrapReject('没有放到正确的位置，请将被拖拽积木块的左端，与目标高亮区域的左端，对齐放置');
                                            return;
                                        }
                                    }else {
                                        wrapReject('错误的参数ID：' + target.input);
                                        return;
                                    }*/
                                }else {
                                    wrapReject('没有放到正确的位置，请将被拖拽积木块的左端，与目标高亮区域的左端，对齐放置');
                                    return;
                                }
                            }else {
                                //校验是否拖动到了内部，而不是接在后面
                                if (parentBlock.next === info.blockId) {
                                    wrapResolve();
                                    return;
                                }else {
                                    wrapReject('请拼接到目标的下面，将被拖拽积木块的左端，与目标高亮区域的左端，对齐放置');
                                    return;
                                }

                            }
                        }else {
                            wrapReject('没有放到正确的位置，请将被拖拽积木块的左端，与目标高亮区域的左端，对齐放置');
                            return;
                        }
                    }
                }else if (target.position) {
                    moveBlockPosition(ScratchBlocks.mainWorkspace.getBlockById(info.blockId), target.position.x, target.position.y);
                    wrapResolve();
                    return;
                }else {
                    wrapReject('没有找到目标块：' + JSON.stringify(target));
                    return;
                }
            }else {
                wrapReject('被拖动的块不正确');
                return;
            }

        };
        event.once(EVENTS.END_DRAG_AND_MOVE, handleEndDrag);

    });
}
